/** What the free trial opens (the agency chooses it: D-110), and the free mini mock of the exam
 * being prepared. */

import { type AcademyOffer, ApiError, useAcademyAttemptsCreate } from "@agencyos/api-client";
import { asTrialOffer, asTrialStatus } from "@agencyos/core";
import { Button } from "@agencyos/ui-web";
import { Check, Gift, Timer } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useActiveExam } from "../ielts/exam";
import { dateText } from "../shared";
import { CardTitle } from "../ui";

export function TrialCard({ offer, trial }: { offer: AcademyOffer; trial?: unknown }) {
  const { t, i18n } = useTranslation();
  const content = asTrialOffer(offer.trial);
  const status = trial === undefined ? undefined : asTrialStatus(trial);
  return (
    <section className="ac-surface ac-card as-trial" aria-labelledby="as-trial-title">
      <CardTitle id="as-trial-title" icon={<Gift size={16} />} tone="success">
        {t("academySales.trial.title")}
      </CardTitle>
      <ul className="as-pass__items">
        {content.placement ? (
          <li>
            <Check aria-hidden size={16} />
            <span>{t("academySales.trial.placement")}</span>
          </li>
        ) : null}
        {content.method ? (
          <li>
            <Check aria-hidden size={16} />
            <span>{t("academySales.trial.method")}</span>
          </li>
        ) : null}
        {content.discovery ? (
          <li>
            <Check aria-hidden size={16} />
            <span>{t("academySales.trial.discovery", { count: content.discovery_questions })}</span>
          </li>
        ) : null}
        {content.days > 0 ? (
          <li>
            <Check aria-hidden size={16} />
            <span>{t("academySales.trial.days", { count: content.days })}</span>
          </li>
        ) : null}
      </ul>
      {status?.trial_until ? (
        <p className="ac-footnote">{t("academySales.trial.until", { date: dateText(status.trial_until, i18n.language) })}</p>
      ) : null}
      {status && content.discovery ? <DiscoveryButton used={status.discovery_used} /> : null}
    </section>
  );
}

/** Starts the free mini mock of the active exam (listening and reading, the real format). */
export function DiscoveryButton({ used }: { used: Record<string, boolean> }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { exam } = useActiveExam();
  const start = useAcademyAttemptsCreate();
  if (used[exam]) return <p className="ac-footnote">{t("academySales.trial.discoveryUsed")}</p>;
  return (
    <div className="as-discovery">
      <Button
        variant="secondary"
        disabled={start.isPending}
        onClick={() =>
          start.mutate(
            { data: { exam, kind: "discovery", skills: ["co", "ce"] } },
            { onSuccess: (response) => response.status === 201 && navigate(`/academy/attempt/${response.data.id}`) },
          )
        }
      >
        <Timer aria-hidden size={16} /> {t("academySales.trial.discoveryCta")}
      </Button>
      <p className="ac-footnote">{t("academySales.trial.discoveryHint")}</p>
      {start.error instanceof ApiError && start.error.detail ? <p className="ko">{start.error.detail}</p> : null}
    </div>
  );
}
